import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-20 text-center">
          <div className="w-16 h-16 bg-[#D9B38C]/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <span className="text-2xl">🌿</span>
          </div>
          <h2 className="text-xl font-bold text-[#1A2E12] mb-2">
            Your skin had a moment — let's reset.
          </h2>
          <p className="text-sm text-[#6B7B6E] mb-6 max-w-sm">
            Refresh the page and your ritual will be waiting.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-[#BDD630] text-[#1A2E12] rounded-full font-semibold text-sm hover:bg-[#97c23c] transition-colors"
          >
            Refresh the Page
          </button>
          {import.meta.env.DEV && this.state.errorInfo && (
            <details className="mt-8 text-left max-w-xl w-full">
              <summary className="text-xs text-[#6B7B6E] cursor-pointer">Debug info</summary>
              <pre className="mt-2 p-3 bg-[#F7F5EE] rounded text-[10px] text-[#1A2E12] overflow-auto max-h-48">
                {this.state.error?.stack}
                {'\n\nComponent Stack:\n'}
                {this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
